(() => {
  const ELEMENT_NAME = 'betterhome-header-v002';

  if (customElements.get(ELEMENT_NAME)) return;

  class BetterHomeHeaderV002 extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.sectionHeader = this.closest('.section-header');
      this.shop = this.querySelector('[data-bh-shop]');
      this.shopSummary = this.querySelector('[data-bh-shop-summary]');
      this.mega = this.querySelector('[data-bh-mega]');
      this.megaBackdrop = this.querySelector('[data-bh-mega-close]');
      this.drawer = this.querySelector('header-drawer');
      this.drawerDetails = this.querySelector('[data-bh-drawer-details]');
      this.drawerSummary = this.querySelector('[data-bh-drawer-summary]');
      this.drawerPanel = this.querySelector('[data-bh-drawer-panel]');
      this.drawerClose = this.querySelector('[data-bh-drawer-close-button]');
      this.drawerBackdrop = this.querySelector('[data-bh-drawer-close]');
      this.accordions = [...this.querySelectorAll('[data-bh-accordion]')];

      this.bindShop();
      this.bindDrawer();
      this.bindGlobal();
      this.syncShopState();
      this.syncDrawerState();
      this.updateLayerTop();
      this.dataset.bhReady = 'true';
    }

    disconnectedCallback() {
      this.closeShop(false);
      document.body.classList.remove('bh-header-v002-drawer-open');
      this.sectionHeader?.classList.remove('bh-header-v002--layer-open');
      this.abortController?.abort();
    }

    bindShop() {
      if (!this.shop || !this.shopSummary) return;

      this.shopSummary.addEventListener(
        'click',
        () => {
          requestAnimationFrame(() => {
            if (this.shop.open) {
              this.closeDrawer(false);
              this.closeSearch();
              this.updateLayerTop();
            }
            this.syncShopState();
          });
        },
        { signal: this.signal },
      );

      this.shop.addEventListener('toggle', () => this.syncShopState(), { signal: this.signal });
      this.megaBackdrop?.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          this.closeShop(true);
        },
        { signal: this.signal },
      );

      this.mega?.addEventListener(
        'click',
        (event) => {
          if (event.target.closest('a')) this.closeShop(false);
        },
        { signal: this.signal },
      );
    }

    bindDrawer() {
      if (!this.drawerDetails || !this.drawerSummary) return;

      this.drawerSummary.addEventListener(
        'click',
        () => {
          requestAnimationFrame(() => {
            if (this.drawerDetails.open) {
              this.closeShop(false);
              this.closeSearch();
              this.openDrawer();
            } else {
              this.syncDrawerState();
            }
          });
        },
        { signal: this.signal },
      );

      this.drawerDetails.addEventListener(
        'toggle',
        () => {
          if (this.drawerDetails.open) this.openDrawer();
          else this.syncDrawerState();
        },
        { signal: this.signal },
      );

      this.drawerClose?.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.closeDrawer(true);
        },
        { capture: true, signal: this.signal },
      );

      this.drawerBackdrop?.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.closeDrawer(true);
        },
        { capture: true, signal: this.signal },
      );

      this.drawerPanel?.addEventListener(
        'click',
        (event) => {
          if (event.target.closest('a')) this.closeDrawer(false);
        },
        { signal: this.signal },
      );

      this.accordions.forEach((accordion) => {
        const summary = accordion.querySelector('summary');
        if (!summary) return;

        summary.setAttribute('aria-expanded', accordion.open ? 'true' : 'false');
        summary.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            accordion.open = !accordion.open;
            summary.setAttribute('aria-expanded', accordion.open ? 'true' : 'false');
          },
          { capture: true, signal: this.signal },
        );
        accordion.addEventListener(
          'toggle',
          () => summary.setAttribute('aria-expanded', accordion.open ? 'true' : 'false'),
          { signal: this.signal },
        );
      });
    }

    bindGlobal() {
      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Escape') return;

          if (this.drawerDetails?.open) {
            event.preventDefault();
            event.stopPropagation();
            this.closeDrawer(true);
            return;
          }

          if (this.shop?.open) {
            event.preventDefault();
            event.stopPropagation();
            this.closeShop(true);
          }
        },
        { capture: true, signal: this.signal },
      );

      document.addEventListener(
        'pointerdown',
        (event) => {
          if (!this.shop?.open) return;
          if (this.shop.contains(event.target)) return;
          if (this.megaBackdrop?.contains(event.target)) return;
          this.closeShop(false);
        },
        { capture: true, signal: this.signal },
      );

      window.addEventListener(
        'resize',
        () => {
          this.updateLayerTop();
          if (window.innerWidth < 1100) this.closeShop(false);
          if (window.innerWidth >= 1100) this.closeDrawer(false);
        },
        { signal: this.signal },
      );

      window.addEventListener('scroll', () => this.updateLayerTop(), {
        passive: true,
        signal: this.signal,
      });
    }

    syncShopState() {
      const open = Boolean(this.shop?.open);
      this.shopSummary?.setAttribute('aria-expanded', String(open));
      this.mega?.setAttribute('aria-hidden', String(!open));
      document.body.classList.toggle('bh-header-v002-mega-open', open);
      this.syncLayerClass();
    }

    closeShop(restoreFocus = false) {
      if (!this.shop?.open) {
        this.syncShopState();
        return;
      }

      this.shop.open = false;
      this.syncShopState();
      if (restoreFocus) this.shopSummary?.focus();
    }

    openDrawer() {
      if (!this.drawerDetails?.open) return;

      document.body.classList.add('bh-header-v002-drawer-open');
      this.drawerSummary?.setAttribute('aria-expanded', 'true');
      this.drawerPanel?.setAttribute('aria-hidden', 'false');
      this.syncLayerClass();

      requestAnimationFrame(() => {
        if (typeof window.trapFocus === 'function' && this.drawerPanel) {
          window.trapFocus(this.drawerPanel, this.drawerClose || this.drawerPanel);
        }
        this.drawerClose?.focus();
      });
    }

    closeDrawer(restoreFocus = false) {
      if (!this.drawerDetails?.open) {
        this.syncDrawerState();
        return;
      }

      if (this.drawer && typeof this.drawer.closeMenuDrawer === 'function') {
        const closeEvent = new Event('click', { bubbles: false });
        this.drawer.closeMenuDrawer(closeEvent, this.drawerSummary);
      } else {
        this.drawerDetails.open = false;
      }

      this.drawerDetails.classList.remove('menu-opening');
      this.syncDrawerState();
      if (restoreFocus) this.drawerSummary?.focus();
    }

    syncDrawerState() {
      const open = Boolean(this.drawerDetails?.open);
      this.drawerSummary?.setAttribute('aria-expanded', String(open));
      this.drawerPanel?.setAttribute('aria-hidden', String(!open));
      document.body.classList.toggle('bh-header-v002-drawer-open', open);
      this.syncLayerClass();
    }

    closeSearch() {
      const searchModal = this.querySelector('details-modal');
      if (searchModal && typeof searchModal.close === 'function') searchModal.close(false);
    }

    syncLayerClass() {
      const open = Boolean(this.shop?.open || this.drawerDetails?.open);
      this.sectionHeader?.classList.toggle('bh-header-v002--layer-open', open);
    }

    updateLayerTop() {
      const headerBar = this.querySelector('.bh-header-v002__bar');
      if (!headerBar) return;
      const bottom = Math.max(0, Math.floor(headerBar.getBoundingClientRect().bottom));
      this.style.setProperty('--bh-header-v002-layer-top', `${bottom}px`);
    }
  }

  customElements.define(ELEMENT_NAME, BetterHomeHeaderV002);
})();
